import { useEffectOnce } from '@cosuno/react-hooks';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { assert } from 'ts-essentials';

import type { GetGoogleMapsApi } from './context';

const SCRIPT_ID = 'gmApiScript';

const injectGoogleApiScript = (apiKey: string, language: string) => {
  const src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=gmApiCallback&language=${language}`;

  if (document.querySelectorAll(`script[src="${src}"]`).length > 0) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('src', src);
  script.setAttribute('id', SCRIPT_ID);

  const first = document.getElementsByTagName('script')[0];
  first.parentNode?.insertBefore(script, first);

  return script;
};

export const useGoogleMapsApiManager = (opts: {
  apiKey: string;
  mock?: boolean;
}): GetGoogleMapsApi => {
  const [state, setState] = useState<'idle' | 'loading' | 'done'>('idle');

  const { i18n } = useTranslation();

  const loadApi = useCallback(() => {
    if (opts.mock) return;

    // Fetch and load Maps API on init and when language changes
    if (window.google?.maps) {
      setState('loading');
      document.getElementById('gmApiScript')?.remove();
      delete (window.google as { maps?: unknown }).maps;
    }

    window.gmApiCallback = () => setState('done');
    injectGoogleApiScript(opts.apiKey, i18n.language as string);
  }, [i18n.language, opts.apiKey, opts.mock]);

  useEffect(() => {
    if (state === 'idle') return;

    i18n.on('languageChanged', loadApi);

    return () => i18n.off('languageChanged', loadApi);
  });

  useEffectOnce(() => {
    if (opts.mock) {
      void import('./mock').then(({ registerMocks }) => {
        requestAnimationFrame(() => {
          // eslint-disable-next-line no-console
          console.log('registered google maps mock');
          registerMocks();
          setState('done');
        });
      });
    }
  });

  // noinspection UnnecessaryLocalVariableJS
  const getGoogleMapsApi = useCallback(() => {
    switch (state) {
      case 'idle': {
        loadApi();
        return null;
      }

      case 'loading':
        return null;

      case 'done': {
        if (opts.mock) {
          assert(window.mocks, '`window.mocks` must be defined');
          return { type: 'mock' as const, mocks: { current: window.mocks } };
        }

        return { type: 'production' as const, maps: window.google.maps };
      }
    }
  }, [loadApi, opts.mock, state]);

  return getGoogleMapsApi;
};
